import { Button, Modal, useToast } from '@citizen-walk/shared-ui';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { RecoveryOverwriteConfirm } from './RecoveryOverwriteConfirm';
import { useRecoverParticipant } from './useRecoverParticipant';

export interface RecoveryRecoverModalProps {
  open: boolean;
  onClose: () => void;
  /** 表示用 (地区名)。 */
  districtName?: string;
  /** 復帰 callable 用 urlToken (district route)。 */
  districtToken?: string;
  /** fallback「新規参加にする」押下時のコールバック (任意、onboarding へ誘導)。 */
  onFallback?: () => void;
  /** 復帰成功時のコールバック (任意、UI で再読込促す等)。 */
  onRecovered?: (result: { recoveredFromUid: string; accessibleDistrictCount: number }) => void;
}

/**
 * リカバリコード入力 + 復帰モーダル (REQ-4.1.5、old2 入口フロー移植 Phase D)。
 *
 * 旧 old2 `apps/user/src/pages/RecoveryRecoverPage.tsx` の分割入力 UI を移植:
 *   - 12 文字コードを XXXX × 3 の分割フィールドに分け、入力ミスを防ぐ。
 *   - 4 文字到達で次フィールドへ自動進行 / Backspace で前フィールドへ戻る / 矢印キーナビ。
 *   - ペーストはハイフン・空白・小文字を吸収して全フィールドへ自動分配。
 *   - ライブプレビュー『XXXX-XXXX-XXXX（n/12）』表示 + 入力クリア。
 *
 * v2 でのコード形式修正 (旧 old2 からの不整合解消):
 *   - **`BMA-` プレフィックス撤去**: old2 の発行コードは `BMA-XXXX-XXXX-XXXX` (BMA + 12 文字、
 *     normalize で hyphen のみ除去 → BMA 込み 15 文字で hash/照合) だった。一方 v2 の
 *     `issueRecoveryCode` は **BMA 無しの 12 文字 raw** を発行し、`recoverParticipant` は
 *     **12 文字ちょうど**を要求する (RecoveryIssuePage の表示も raw 12 文字)。
 *     旧コードは本 modal が old2 逐語移植だったため `BMA-XXXX-XXXX-XXXX` (18 文字) をサーバへ送り、
 *     サーバの長さ検証 (=12) で必ず弾かれていた (= 手入力復帰が機能していなかった)。
 *     v2 はコードに固定 prefix を持たないため、固定枠 BMA を撤去し、送信値は **3 ブロック連結の
 *     12 文字 raw** (`parts.join('')`) とする。`stripPrefix('BMA')` も撤去 (v2 コードは偶然 B/M/A で
 *     始まりうるため、prefix 除去は誤って正規コードを壊す)。
 *   - **Page → Modal 化** (shared-ui Modal、AppHome から開ける)。
 *   - 復帰は `useRecoverParticipant` callable hook 経由 (成功時 ID トークン refresh は hook 内)。
 */

const ALPHABET_RE = /[A-Z2-9]/;

function sanitize(raw: string): string {
  return raw
    .toUpperCase()
    .split('')
    .filter((c) => ALPHABET_RE.test(c))
    .join('');
}

export function RecoveryRecoverModal({
  open,
  onClose,
  districtName,
  districtToken,
  onFallback,
  onRecovered,
}: RecoveryRecoverModalProps) {
  const { state, call, confirm, reset } = useRecoverParticipant();
  const { toast } = useToast();
  const [parts, setParts] = useState<[string, string, string]>(['', '', '']);

  const ref0 = useRef<HTMLInputElement>(null);
  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);

  // tuple index access (noUncheckedIndexedAccess) を避けるため switch で ref を解決する。
  const refAt = useCallback((idx: number): HTMLInputElement | null => {
    switch (idx) {
      case 0:
        return ref0.current;
      case 1:
        return ref1.current;
      case 2:
        return ref2.current;
      default:
        return null;
    }
  }, []);

  // v2 のコードは BMA 無し・ハイフン無しの 12 文字 raw。3 ブロックを連結してサーバへ送る。
  const code = parts.every((p) => p.length === 4) ? parts.join('') : '';

  // モーダル閉じた時に state + 入力をリセット
  useEffect(() => {
    if (!open) {
      reset();
      setParts(['', '', '']);
    }
  }, [open, reset]);

  // 開いたら先頭フィールドへフォーカス
  useEffect(() => {
    if (open) refAt(0)?.focus();
  }, [open, refAt]);

  // 成功/失敗を Toast 通知 (status 変化のみ監視で二重発火回避)
  const status = state.status;
  const result = state.status === 'success' ? state.data : null;
  useEffect(() => {
    if (status === 'success' && result) {
      // 解釈B: 復帰は常に 1 地区 (urlToken の地区) のみ。accessibleDistrictCount は復帰先 uid の
      // 合計アクセス地区数で「復帰した地区数」ではないため、誤解を避け地区数を出さない文言にする。
      if (result.noop) {
        // 同一 uid (自分のコードを自分の端末で入力): 変更なし。
        toast('info', 'この端末はすでにこの参加情報を使っています');
      } else {
        toast('success', 'この地区の参加状態を復帰しました');
      }
      onRecovered?.({
        recoveredFromUid: result.recoveredFromUid,
        accessibleDistrictCount: result.accessibleDistrictCount,
      });
    }
  }, [status, result, toast, onRecovered]);

  function setPart(idx: 0 | 1 | 2, v: string) {
    const next = [...parts] as [string, string, string];
    next[idx] = v.slice(0, 4);
    setParts(next);
    if (next[idx].length === 4 && idx < 2) {
      const nextRef = refAt(idx + 1);
      nextRef?.focus();
      nextRef?.select();
    }
  }

  function distributeFromPaste(text: string, startIdx: 0 | 1 | 2) {
    // v2 コードは prefix を持たないため sanitize のみ (ハイフン/空白/小文字を吸収)。
    const cleaned = sanitize(text);
    if (cleaned.length === 0) return;
    const next = [...parts] as [string, string, string];
    let i = startIdx;
    let j = 0;
    while (i < 3 && j < cleaned.length) {
      const cur = next[i] ?? '';
      const room = 4 - cur.length;
      const take = cleaned.slice(j, j + room);
      next[i] = (cur + take).slice(0, 4);
      j += take.length;
      if ((next[i] ?? '').length === 4) i++;
    }
    setParts(next);
    const focusIdx = Math.min(i, 2) as 0 | 1 | 2;
    const focusRef = refAt(focusIdx);
    focusRef?.focus();
    if ((next[focusIdx] ?? '').length === 4) focusRef?.select();
  }

  function onPartChange(idx: 0 | 1 | 2, raw: string) {
    if (state.status === 'error') reset();
    const cleaned = sanitize(raw);
    // ユーザーがハイフン込み／12 文字一括で貼った場合に備えて全体を再分配する。
    if (idx === 0 && (cleaned.length > 4 || raw.includes('-'))) {
      distributeFromPaste(raw, 0);
      return;
    }
    setPart(idx, cleaned);
  }

  function onKeyDown(idx: 0 | 1 | 2, e: KeyboardEvent<HTMLInputElement>) {
    const cur = parts[idx] ?? '';
    if (e.key === 'Backspace' && cur.length === 0 && idx > 0) {
      refAt(idx - 1)?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && (e.currentTarget.selectionStart ?? 0) === 0 && idx > 0) {
      refAt(idx - 1)?.focus();
      e.preventDefault();
    } else if (
      e.key === 'ArrowRight' &&
      (e.currentTarget.selectionStart ?? 0) === cur.length &&
      idx < 2
    ) {
      refAt(idx + 1)?.focus();
      e.preventDefault();
    }
  }

  function onPaste(idx: 0 | 1 | 2, e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    e.preventDefault();
    distributeFromPaste(text, idx);
  }

  function clearAll() {
    setParts(['', '', '']);
    if (state.status === 'error') reset();
    refAt(0)?.focus();
  }

  function recover() {
    if (!code) return;
    // 解釈B: 復帰は地区スコープ。urlToken (districtToken) 必須。未指定なら復帰しない。
    if (!districtToken) {
      toast('error', '地区情報が取得できませんでした。地区の URL から開き直してください。');
      return;
    }
    void call({ recoveryCode: code, urlToken: districtToken });
  }

  function fallback() {
    if (onFallback) onFallback();
    else onClose();
  }

  const submitting = state.status === 'pending';
  const inputClass =
    'w-full min-h-touch px-2 border border-slate-300 rounded-md text-xl text-center font-mono uppercase tracking-[0.2em] focus:outline-none focus:border-primary-600 focus:ring-2 focus:ring-primary-100';

  return (
    <Modal open={open} onClose={onClose} title="🔑 ユーザー復帰コードで復帰" size="md">
      {districtName && <p className="text-sm text-slate-600 mb-1">📍 {districtName}</p>}

      <p className="text-slate-700 mb-1 text-sm">
        以前発行された 12 文字のユーザー復帰コードを入力してください。
      </p>
      <p className="text-xs text-slate-500 mb-4">
        4 文字ごとに自動で次の枠へ進みます。コードを丸ごと貼り付けると自動で分配されます。
      </p>

      <div className="flex items-center gap-2 mb-2">
        <input
          ref={ref0}
          value={parts[0]}
          onChange={(e) => onPartChange(0, e.target.value)}
          onKeyDown={(e) => onKeyDown(0, e)}
          onPaste={(e) => onPaste(0, e)}
          onFocus={(e) => e.target.select()}
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          maxLength={4}
          placeholder="XXXX"
          aria-label="ユーザー復帰コード 第1ブロック"
          className={inputClass}
        />
        <span className="text-slate-400 text-lg">-</span>
        <input
          ref={ref1}
          value={parts[1]}
          onChange={(e) => onPartChange(1, e.target.value)}
          onKeyDown={(e) => onKeyDown(1, e)}
          onPaste={(e) => onPaste(1, e)}
          onFocus={(e) => e.target.select()}
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          maxLength={4}
          placeholder="XXXX"
          aria-label="ユーザー復帰コード 第2ブロック"
          className={inputClass}
        />
        <span className="text-slate-400 text-lg">-</span>
        <input
          ref={ref2}
          value={parts[2]}
          onChange={(e) => onPartChange(2, e.target.value)}
          onKeyDown={(e) => onKeyDown(2, e)}
          onPaste={(e) => onPaste(2, e)}
          onFocus={(e) => e.target.select()}
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          maxLength={4}
          placeholder="XXXX"
          aria-label="ユーザー復帰コード 第3ブロック"
          className={inputClass}
        />
      </div>

      <div className="flex items-center justify-between mb-4">
        <button
          onClick={clearAll}
          type="button"
          className="text-xs text-slate-500 underline hover:text-slate-700"
        >
          入力をクリア
        </button>
        <span className="text-xs text-slate-500 font-mono">
          {parts.map((p) => p.padEnd(4, '_')).join('-')}（{parts.join('').length}/12）
        </span>
      </div>

      {state.status === 'needs-confirm' ? (
        // R-70 P6: この端末に別人の参加情報がある。了承なしには置き換えない。
        <div className="mt-2">
          <RecoveryOverwriteConfirm
            existingNickname={state.existingNickname}
            incomingNickname={state.incomingNickname}
            onConfirm={() => void confirm()}
            onCancel={reset}
          />
        </div>
      ) : (
        <>
          {state.status === 'error' && (
            <p className="text-danger-700 mt-2 text-sm">
              {/* 管理者アカウントのコード (2026-10-22 H1) はサーバの日本語文言をそのまま出す */}
              {(state.error as Error & { code?: string }).code?.includes('permission-denied')
                ? state.error.message
                : 'コードが見つかりません。もう一度お試しください。'}
            </p>
          )}

          <button
            onClick={recover}
            disabled={!code || submitting}
            className="w-full mt-2 bg-primary-600 text-white py-4 rounded-md font-medium disabled:opacity-40"
          >
            {submitting ? '確認中…' : '復帰する'}
          </button>

          <Button variant="secondary" size="md" onClick={fallback} className="w-full mt-2">
            新規参加にする
          </Button>
        </>
      )}
    </Modal>
  );
}
